import { defineStore } from 'pinia';
import { cancelInFlightRequests, request, wpApiRequest } from '@/services/requestService';
import { schoolParterStatus, schoolsOnboardingStatus, schoolTier, type SchoolExtended, type SchoolFlags, type SchoolsOnboardingStatus } from '@/interfaces/schoolInterface';
import { type UserToggles, userRole, type User, type UserStore, type UserRole } from '@/interfaces/userInterface';
import { getKeys } from '@/helpers';
import type { RouteLocationRaw } from 'vue-router';

const initialUserData: User = {
	token: '',
	email: '',
	id: 0,
	username: '',
	firstname: '',
	lastname: '',
	role: '' as UserRole,
	schools: [] as SchoolExtended[],
	resourceFavourites: {},
	toggles: {} as UserToggles,
	courseIds: [],
	cluster: '',
};

const lmsOnlineCurriculumId = parseInt(import.meta.env.VITE_LMS_ONLINE_CURRICULUM_ID || '0', 10);
const lmsStudentBehaviourPhdId = parseInt(import.meta.env.VITE_LMS_STUDENT_BEHAVIOUR_PHD_ID || '0', 10);
const courseIdsToNameMap = {
	[lmsOnlineCurriculumId]: 'Online Curriculum',
	[lmsStudentBehaviourPhdId]: 'Student Behaviour PhD',
};

export const useUser = defineStore('user', {
	state: (): User & UserStore => {
		return {
			// Deep clone this (so that things like resourceFavoutires or toggles are not copied between users)
			...JSON.parse(JSON.stringify(initialUserData)),
			currentSchoolId: 0,
			loginLoading: false,
			isReloading: false,
		};
	},
	getters: {
		fullName(state): string {
			const names = [];
			if (state.firstname) {
				names.push(state.firstname);
			}
			if (state.lastname) {
				names.push(state.lastname);
			}
			return names.join(' ');
		},
		isLoggedIn(state): boolean {
			return !!state.token;
		},
		isAdmin(): boolean {
			return this.role === userRole.Administrator;
		},
		isEf(): boolean {
			return this.role === userRole.Ef;
		},
		isOperations(): boolean {
			return this.role === userRole.Operations;
		},
		isChampion(): boolean {
			return this.role === userRole.Champion;
		},
		isTeacher(): boolean {
			return this.role === userRole.Teacher;
		},
		isAdminEf(): boolean {
			// Operations are central RS staff with EF-level access across all schools.
			return this.isAdmin || this.isEf || this.isOperations;
		},
		isAdminEfChampion(): boolean {
			return this.isAdminEf || this.isChampion;
		},
		/**
		 * Central RS staff only, and narrower than isAdminEf: an Expert Facilitator is not
		 * operations. Nav visibility only — it decides who sees the Ops group, not who may
		 * reach anything in it, and the one item there (Activity) is permitted at EF level by
		 * both its route guard and ActivityController. So this hides a link an EF is allowed
		 * to follow; it is not a permission.
		 */
		canAccessOps(): boolean {
			return this.isAdmin || this.isOperations;
		},
		/**
		 *
		 * @returns If the user has 1 school, it returns that school. If the user has multiple schools, they are probably an admin or EF
		 * and we will return the school that is currently selected, or null if nothing selected.
		 *
		 * This is Different to `schools` which will always have all the schools the user has access to.
		 */
		school(): null | SchoolExtended {
			if (this.schools.length <= 0) {
				return null;
			}

			if (this.schools.length === 1) {
				return this.schools[0];
			}

			if (!this.currentSchoolId) {
				return null;
			}

			return this.schools.find((s) => s.ID === this.currentSchoolId) || null;
		},
		/**
		 * Specifically if the current user is part of a partner school
		 */
		isPartner(): boolean {
			return this.school?.partner_status === schoolParterStatus.Partner;
		},
		isPap(): boolean {
			return this.school?.partner_status === schoolParterStatus.PortalAccessPackage;
		},
		canAccessStaffroom(): boolean {
			if (this.isInsideOnboarding) {
				return false;
			}

			return this.isPartner || this.isPap;
		},
		canAccessStaffroomChampion(): boolean {
			return this.canAccessStaffroom && this.isAdminEfChampion;
		},
		/**
		 * The Virtual Partnership Accelerator pages appear in the champion nav for
		 * champions/principals, one item each, split on the school's partnership year. These
		 * are nav getters only — they decide which single VPA item to show, never who may
		 * open the page. The routes themselves are unguarded, so anyone with the link
		 * reaches either year; admins and EFs go via the admin section.
		 *
		 * Everything past the first year gets the Year 2/3 page, extension years included:
		 * a year 5 school is continuing a partnership, which is what that page is about,
		 * and the alternative was 19 schools with no VPA at all. So the pair is exhaustive —
		 * `year` is never below 1, the sync floors it — and every champion sees exactly one.
		 */
		/**
		 * One page for every year now. The two getters this replaces split
		 * champions by partnership year because there were two pages with two
		 * session lists; there is one of each, and the page branches its own
		 * copy on the year.
		 */
		canAccessVpa(): boolean {
			return this.canAccessStaffroomChampion;
		},
		canAccessRcp(): boolean {
			if (!this.canAccessStaffroom) {
				return false;
			}

			return this.school?.tier !== schoolTier.Supported;
		},
		canAccessDashboard(): boolean {
			return this.canAccessStaffroomChampion && this.isPartner;
		},
		isInsideOnboarding(): boolean {
			if (!this.canAccessOnboarding) {
				return false;
			}

			if (!this.school) {
				return false;
			}

			return this.school.onboarding_status < schoolsOnboardingStatus.Complete;
		},
		canAccessOnboarding(): boolean {
			if (!this.school) {
				return false;
			}

			return this.school.onboarding_status && this.school.onboarding_status !== schoolsOnboardingStatus.Hidden;
		},
		/**
		 * The portal proper: everything that isn't a public link. A user can be logged in and have
		 * none of it — Hubspot sync removes them from their school while their account survives.
		 *
		 * Deliberately not consulted on public routes. A page a logged out stranger may open is a
		 * page this user may open too, and gating those on a token instead meant logging in showed
		 * them less than logging out did.
		 */
		canAccessPortal(): boolean {
			if (!this.isLoggedIn) {
				return false;
			}

			if (this.isAdminEf) {
				return true;
			}

			if (this.schools.length > 0) {
				return true;
			}

			return this.canAccessPartnershipCluster;
		},
		canAccessResources(): boolean {
			// Not about onboarding: someone with no portal access at all can only ever see a
			// resource they've been sent a link to, so anything pointing them at the resource
			// list is a link to `no-access`.
			if (!this.canAccessPortal) {
				return false;
			}

			if (!this.school) {
				return true;
			}

			if (!this.isInsideOnboarding) {
				return true;
			}

			return this.school.onboarding_status >= schoolsOnboardingStatus.ExploreResources;
		},
		canAccessLearning(): boolean {
			if (this.isInsideOnboarding) {
				return false;
			}

			if (this.isAdminEf) {
				return true;
			}

			return this.courseIds.length > 0;
		},
		canChooseSchool(): boolean {
			return this.schools.length > 1;
		},
		courseNames(): { id: number; name: string }[] {
			return this.courseIds.map((courseId) => {
				const courseName = courseIdsToNameMap[courseId] || `Course ${courseId}`;
				return {
					id: courseId,
					name: courseName,
				};
			});
		},
		/**
		 * If the user has a cluster value, then they can always see that cluster.
		 *
		 * They don't need to be logged in or assoicated to a school.
		 *
		 * If they are admin/EF, they can also access any cluster if they have a school selected.
		 */
		canAccessPartnershipCluster(): boolean {
			// TODO
			// Remove Admin/EF check
			// Note that we are using window.location here because this is used early in app load before router is initted
			const urlParams = new URLSearchParams(window.location.search);
			if (!this.isAdminEf && !urlParams.get('testPartnershipCluster')) {
				return false;
			}

			if (this.isInsideOnboarding) {
				return false;
			}

			if (this.cluster) {
				return true;
			}

			if (this.isAdminEf && this.school?.cluster) {
				return true;
			}

			return false;
		},
		canAccessPartnership(): boolean {
			if (!this.canAccessStaffroomChampion) {
				return false;
			}

			if (!this.canAccessDashboard && !this.canAccessPartnershipCluster) {
				return false;
			}

			if (this.isAdminEf) {
				return true;
			}

			// Test param for development (champions only reach here)
			const urlParams = new URLSearchParams(window.location.search);
			if (urlParams.get('testPartnership')) {
				return true;
			}

			return this.school?.flags?.partnership_enabled === true;
		},
	},
	actions: {
		async login(userCreds: { username: string; password: string }, logInAs: string = '') {
			this.loginLoading = true;
			let url = '/jwt-auth/v1/token';
			if (logInAs) {
				url += `?log_in_as=${encodeURIComponent(logInAs)}`;
			}

			const res = await wpApiRequest<User>(url, userCreds);
			if (!res.success || !res.data.token) {
				this.loginLoading = false;
				return false;
			}

			this.$patch({ ...res.data });

			await this.$router.push({
				name: 'home',
				query: { ...this.$router.currentRoute.value.query },
			});

			this.loginLoading = false;
		},
		async logout(redirectTo: RouteLocationRaw | null = { name: 'login' }) {
			// Before clearing anything: a request still on its way would otherwise
			// come back after this and write the old user's data back into the store.
			cancelInFlightRequests();

			// Also deep clone it here
			this.$patch({ ...JSON.parse(JSON.stringify(initialUserData)) });
			sessionStorage.removeItem('rs-session-id');

			if (redirectTo) {
				this.$router.push(redirectTo);
				return;
			}
		},
		setSchool(school: SchoolExtended) {
			this.currentSchoolId = school.ID;
		},
		unsetSchool() {
			this.currentSchoolId = 0;
		},
		setSchoolIdFromUrl() {
			// Don't use VueRouter because it might not be initted here yet and we don't
			// want to have to wait for it, since we want the school to be set before
			// router is initted
			const params = new URL(document.location.toString()).searchParams;
			const schoolId = parseInt(params.get('schoolId') ?? '');
			if (schoolId) {
				this.currentSchoolId = schoolId;
			}
		},
		async updateSchoolFlag(key: keyof SchoolFlags, value: boolean) {
			if (!this.school) return;

			const res = await request<{ flags: SchoolFlags }>('school', 'updateSchoolFlag', {
				key,
				value,
			});

			if (res.success && res.data?.flags) {
				this.school.flags = res.data.flags;
			}

			return res;
		},
		async updateToggles(toggleName: keyof UserToggles, value: boolean) {
			await request('user', 'updateToggles', {
				toggleName,
				value,
			});

			this.toggles[toggleName] = value;
		},
		async resetToggles(toValue = false) {
			await request('user', 'resetToggles', {
				toValue,
			});

			getKeys(this.toggles).forEach((k) => {
				this.toggles[k] = toValue;
			});
		},
		triggerResourceView() {
			if (!this.school) {
				return;
			}

			if (!this.isInsideOnboarding) {
				return;
			}

			if (this.school.onboarding_status === schoolsOnboardingStatus.ExploreResources) {
				this.setSchoolOnboardingStatus(schoolsOnboardingStatus.Complete);
			}
		},
		async setSchoolOnboardingStatus(status: SchoolsOnboardingStatus) {
			if (!this.school) {
				return;
			}

			// Set the status and ask for forgiveness if it fails
			this.school.onboarding_status = status;

			const res = await request('school', 'setOnboardingStatus', {
				status,
			});

			if (!res.success) {
				throw new Error(`School ${this.school?.ID} could not be updated to status ${status}.`);
			}
		},
		async reloadData() {
			const auth = useUser();
			if (!auth.isLoggedIn) {
				return;
			}

			if (this.isReloading) {
				return;
			}

			this.isReloading = true;

			const res = await request<{ user: User }>('user', 'getData');

			this.isReloading = false;

			if (!res.success) {
				// TODO
				// handle this error?
				return;
			}

			const currentToken = this.token;

			this.$patch({ ...res.data.user, token: currentToken });
		},
		async logInToLearningSite(redirect?: string): Promise<LmsLoginRedirectResponse | false> {
			if (!this.isLoggedIn) {
				return false;
			}

			const res = await request<LmsLoginRedirectResponse>('user', 'logInToLearningSite', {
				redirect,
			});

			if (!res.success) {
				return false;
			}

			return res.data;
		},
		async requestPasswordReset(email: string, isSetPassword = false) {
			return await request('publicAuth', 'requestPasswordReset', {
				email,
				isSetPassword,
			});
		},
		async resetPassword(key: string, newPassword: string) {
			const res = await request<ResetPasswordResponse>('publicAuth', 'resetPassword', {
				key,
				newPassword,
			});

			return res;
		},
		async updateName(firstname: string, lastname: string) {
			const res = await request<User>('user', 'updateName', {
				firstname,
				lastname,
			});

			if (res.success) {
				this.$patch({
					firstname,
					lastname,
				});
			}

			return res;
		},
		async updatePassword(password: string) {
			const res = await request<User>('user', 'updatePassword', {
				password,
			});
			return res;
		},
	},
	persist: {
		key: 'rs-auth-user-v-2.0',
		omit: ['loginLoading', 'isReloading'],
	},
});

export interface LmsLoginRedirectResponse {
	redirectUrl: string;
}

export interface ResetPasswordResponse {
	email: string;
}
